/* eslint-disable react/prop-types */
import React, { useLayoutEffect, useRef, useState, useEffect } from 'react'
import styled from 'styled-components'
import { createChart, IChartApi, ISeriesApi, CandlestickData, Time, OhlcData } from 'lightweight-charts'
import MarsLogo from '../../assets/images/mars-logo.png'
import Modal from '../Modal'

const TradingViewContainer = styled.div`
  width: 100%;
  height: 600px;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(to bottom, #000000, #231f20);
  box-shadow: 0 10px 9.4px -2px rgba(0, 0, 0, 0.25);
  padding: 20px;

  @media (max-width: 768px) {
    margin: 0;
    border-radius: 32px;
  }
`

const ChartContainer = styled.div`
  width: 100%;
  height: calc(100% - 120px);
  position: relative;

  @media (max-width: 768px) {
    height: calc(100% - 180px);
  }
`

const HorizontalLine = styled.div`
  width: 36px;
  height: 4px;
  background-color: #fff;
  opacity: 0.3;
  margin: 0 auto 10px;
`

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100% + 80px);
  color: ${({ theme }) => theme.text2};
  font-size: 14px;
`

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${({ theme }) => theme.red1};
  font-size: 14px;
  text-align: center;
  padding: 20px;
`

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 0 12px 8px;
  background: linear-gradient(to bottom, #000000, #231f20);
  border-radius: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`

const TokenPairInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const TokenIcons = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`

const TokenIcon = styled.div<{ color: string; zIndex: number }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  color: white;
  position: relative;
  z-index: ${({ zIndex }) => zIndex};
  margin-left: ${({ zIndex }) => (zIndex === 2 ? '-8px' : '0')};
  border: 2px solid ${({ theme }) => theme.bg1};
`

const SwapIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  background-color: #e7ac08;
  border-radius: 50%;

  @media (max-width: 1536px) {
    width: 20px;
    height: 20px;
  }
`

const SwapIcon = styled.img`
  width: 16px;
  height: 16px;
  rotate: 90deg;

  @media (max-width: 1536px) {
    width: 12px;
    height: 12px;
  }
`

const PairName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.text1};
`

const StatsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap-reverse;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: flex-start;
    flex-wrap: wrap-reverse;
  }
`

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`

const StatLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.text2};
  margin-bottom: 2px;
`

const StatValue = styled.div<{ isChange?: boolean; positive?: boolean }>`
  font-size: ${({ isChange }) => (isChange ? '14px' : '16px')};
  font-weight: 600;
  color: ${({ theme, isChange, positive }) => (isChange ? (positive ? theme.green1 : theme.red1) : theme.text1)};
`

const CurrentPrice = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: ${({ theme }) => theme.text1};
  margin-left: 10px;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    text-align: left;
  }
`

const TimeframeSelector = styled.div`
  position: relative;
  display: inline-block;
`

const SelectButton = styled.button`
  padding: 8px 12px;
  border: none;
  background: #231f20;
  color: ${({ theme }) => theme.text1};
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 60px;
  justify-content: space-between;

  &:hover {
    background: ${({ theme }) => theme.bg2};
  }
`

const SelectMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100px;
  background: ${({ theme }) => theme.bg1};
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  margin-top: 4px;
  max-height: 200px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 2px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.bg3};
  }
`

const SelectItem = styled.div<{ active: boolean }>`
  padding: 8px 12px;
  cursor: pointer;
  font-size: 12px;
  color: ${({ theme }) => theme.text1};
  background: ${({ theme, active }) => (active ? theme.bg2 : 'transparent')};

  &:hover {
    background: ${({ theme }) => theme.bg2};
  }
`

const OHLCContainer = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background: transparent;
  border: none;
  padding: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.text1};
  z-index: 10;
  min-width: auto;
`

const TradingPairInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 600;
`

const PairSymbol = styled.span`
  color: ${({ theme }) => theme.text1};
`

const TimeframeDot = styled.span`
  color: ${({ theme }) => theme.text2};
  font-size: 12px;
`

const PlatformName = styled.span`
  color: ${({ theme }) => theme.text2};
  font-size: 12px;
`

const StatusIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.green1};
  margin-left: 4px;
`

const OHLCValues = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;

  @media (max-width: 768px) {
    width: 65vw;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
    box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const OHLCItem = styled.span<{ isPositive?: boolean }>`
  color: ${({ theme, isPositive }) =>
    isPositive === undefined ? theme.text1 : isPositive ? theme.green1 : theme.red1};
  @media (max-width: 768px) {
    margin-left: 4px;
    font-size: 0.63rem;
  }
`

const ChangeValue = styled.span<{ positive: boolean }>`
  color: ${({ theme, positive }) => (positive ? theme.green1 : theme.red1)};
  font-weight: 600;
`

interface TokenInfo {
  symbol: string | undefined
  address: string
  decimals: number
  chainId: number
}

interface TradingViewProps {
  symbol?: string
  pairAddress?: string | null
  inputToken?: TokenInfo | null
  outputToken?: TokenInfo | null
  theme?: 'light' | 'dark'
  interval?: string
  autosize?: boolean
  height?: number
  onShowChart?: () => void
  shouldInvertPrices?: boolean
}

// 使用 OhlcData 接口定义K线数据类型
type KlineData = OhlcData<Time>

const TradingView: React.FC<TradingViewProps> = ({
  symbol = 'MOVA/TTA',
  pairAddress = null,
  inputToken = null,
  outputToken = null,
  theme = 'dark',
  interval = '1d',
  autosize = true,
  height = 600,
  onShowChart,
  shouldInvertPrices = false
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentTimeframe, setCurrentTimeframe] = useState('15m')
  const [, setChartData] = useState<KlineData[]>([])
  const [currentOHLC, setCurrentOHLC] = useState<{
    open: number
    high: number
    low: number
    close: number
    change: number
    changePercent: number
  } | null>(null)
  const [hoveredOHLC, setHoveredOHLC] = useState<{
    open: number
    high: number
    low: number
    close: number
    change: number
    changePercent: number
    time: number
  } | null>(null)

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [currentPrice, setCurrentPrice] = useState<number | null>(null)
  const [priceChangePercent24h, setPriceChangePercent24h] = useState<number>(0)
  const [high24h, setHigh24h] = useState<number>(0)
  const [low24h, setLow24h] = useState<number>(0)
  const [isMobile, setIsMobile] = useState(false)

  // 检测屏幕宽度
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => {
      window.removeEventListener('resize', checkScreenSize)
    }
  }, [])

  // 默认timeframes，如果API没有返回数据则使用这个
  const [timeframes, setTimeframes] = useState<{ label: string; value: string }[]>([
    { label: '1 minute', value: '1m' },
    { label: '5 minutes', value: '5m' },
    { label: '15 minutes', value: '15m' },
    { label: '30 minutes', value: '30m' },
    { label: '1 hour', value: '1h' },
    { label: '1 day', value: '1d' },
    { label: '1 week', value: '1w' },
    { label: '1 month', value: '1M' }
  ])

  // 获取K线数据
  const fetchKlineData = async (timeframe: string, shouldInvert = false) => {
    console.log(timeframe)
    try {
      const now = Math.floor(Date.now() / 1000)
      // const from = now - (timeframe === '1M' ? 86400 * 365 : timeframe === '1w' ? 86400 * 30 : 86400 * 7)
      // let from
      // switch (timeframe) {
      //   case '1m':
      //     from = now - 60
      //     break
      //   case '5m':
      //     from = now - 300
      //     break
      //   case '15m':
      //     from = now - 900
      //     break
      //   case '30m':
      //     from = now - 1800
      //     break
      //   case '1h':
      //     from = now - 3600
      //     break
      //   case '1d':
      //     from = now - 86400
      //     break
      //   case '1w':
      //     from = now - 604800
      //     break
      //   case '1M':
      //     from = now - 2592000
      //     break
      //   default:
      //     from = now - 86400
      // }

      const from = '1754755200'

      const params = new URLSearchParams({
        symbol: pairAddress || symbol,
        resolution: timeframe,
        from: from.toString(),
        to: now.toString()
      })

      const apiUrl = `https://zilwwyrgetjplvwcowjl.supabase.co/functions/v1/usd1swap-candles/history?${params}`

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000)

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InppbHd3eXJnZXRqcGx2d2Nvd2psIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTU4ODI5NTQsImV4cCI6MjA3MTQ1ODk1NH0.iLGNdvqD1fuwUJjgbzhe4Mz_jbkIl3K_bCYroCs3QCE`
        },
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`)
      }

      const data = await response.json()

      if (data.s === 'ok' && data.t && data.t.length > 0) {
        const fromAddress = inputToken?.address?.toLowerCase()
        const toAddress = outputToken?.address?.toLowerCase()

        const bars: KlineData[] = data.t.map((time: number, index: number) => {
          const open = data.o[index]
          const high = data.h[index]
          const low = data.l[index]
          const close = data.c[index]

          let adjustedOpen = open
          let adjustedHigh = high
          let adjustedLow = low
          let adjustedClose = close

          if (fromAddress && toAddress && fromAddress > toAddress) {
            adjustedOpen = 1 / open
            adjustedHigh = 1 / low
            adjustedLow = 1 / high
            adjustedClose = 1 / close
          }

          return {
            time: time,
            open: adjustedOpen,
            high: adjustedHigh,
            low: adjustedLow,
            close: adjustedClose
          }
        })

        // 设置当前OHLC数据
        if (bars.length > 0) {
          const latest = bars[bars.length - 1]
          const previous = bars.length > 1 ? bars[bars.length - 2] : latest
          const change = latest.close - previous.close
          const changePercent = (change / previous.close) * 100

          setCurrentOHLC({
            open: latest.open,
            high: latest.high,
            low: latest.low,
            close: latest.close,
            change: change,
            changePercent: changePercent
          })

          // 设置当前价格和24h数据
          setCurrentPrice(latest.close)
          setHigh24h(Math.max(...bars.map(bar => bar.high)))
          setLow24h(Math.min(...bars.map(bar => bar.low)))
          setPriceChangePercent24h(changePercent)
        }

        setChartData(bars)
        return bars
      } else {
        return []
      }
    } catch (error) {
      console.error('Error fetching kline data:', error)
      let errorMessage = '数据获取失败'

      if ((error as Error).name === 'AbortError') {
        errorMessage = '请求超时，请检查网络连接'
      } else if ((error as Error).message.includes('Failed to fetch')) {
        errorMessage = '网络连接失败，请检查网络或API服务状态'
      } else if ((error as Error).message.includes('CORS')) {
        errorMessage = 'CORS错误，请检查API配置'
      } else {
        errorMessage = `数据获取失败: ${(error as Error).message}`
      }

      console.log('errorMessage', errorMessage)
      throw error
    }
  }

  // 测试UDF API连接
  const testUDFConnection = async () => {
    try {
      const testUrl = 'https://zilwwyrgetjplvwcowjl.supabase.co/functions/v1/usd1swap-candles/config'

      const configResponse = await fetch(testUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InppbHd3eXJnZXRqcGx2d2Nvd2psIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTU4ODI5NTQsImV4cCI6MjA3MTQ1ODk1NH0.iLGNdvqD1fuwUJjgbzhe4Mz_jbkIl3K_bCYroCs3QCE`
        }
      })

      if (!configResponse.ok) {
        throw new Error(`Config API error: ${configResponse.status} - ${configResponse.statusText}`)
      }

      const config = await configResponse.json()

      // 从config API读取支持的resolutions
      if (config.supported_resolutions && Array.isArray(config.supported_resolutions)) {
        const timeframesFromConfig = config.supported_resolutions.map((res: string) => {
          let label = res

          if (res.includes('m')) {
            const minutes = res.replace('m', '')
            label = `${minutes} minute${minutes !== '1' ? 's' : ''}`
          } else if (res.includes('h')) {
            const hours = res.replace('h', '')
            label = `${hours} hour${hours !== '1' ? 's' : ''}`
          } else if (res.includes('d')) {
            const days = res.replace('d', '')
            label = `${days} day${days !== '1' ? 's' : ''}`
          } else if (res.includes('w')) {
            const weeks = res.replace('w', '')
            label = `${weeks} week${weeks !== '1' ? 's' : ''}`
          } else if (res.includes('M')) {
            const months = res.replace('M', '')
            label = `${months} month${months !== '1' ? 's' : ''}`
          }

          return { label, value: res }
        })

        setTimeframes(timeframesFromConfig)
      }

      return true
    } catch (error) {
      console.error('UDF连接测试失败:', error)
      let errorMessage = 'UDF连接失败'

      if ((error as Error).message.includes('Failed to fetch')) {
        errorMessage = '网络连接失败，请检查网络或API服务状态'
      } else if ((error as Error).message.includes('CORS')) {
        errorMessage = 'CORS错误，请检查API配置'
      } else {
        errorMessage = `UDF连接失败: ${(error as Error).message}`
      }
      console.log('errorMessage', errorMessage)

      return false
    }
  }

  // 初始化图表
  const initializeChart = () => {
    if (!chartContainerRef.current) return

    try {
      const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: chartContainerRef.current.clientHeight,
        layout: {
          background: { color: theme === 'dark' ? '#231f20' : '#ffffff' },
          textColor: theme === 'dark' ? '#d1d4dc' : '#131722'
        },
        grid: {
          vertLines: { color: theme === 'dark' ? '#363a45' : '#e1e3e6' },
          horzLines: { color: theme === 'dark' ? '#363a45' : '#e1e3e6' }
        },
        crosshair: {
          mode: 1
        },
        rightPriceScale: {
          borderColor: theme === 'dark' ? '#363a45' : '#e1e3e6',
          autoScale: true,
          scaleMargins: {
            top: 0.1,
            bottom: 0.1
          }
        },
        timeScale: {
          // barSpacing: 15,
          borderColor: theme === 'dark' ? '#363a45' : '#e1e3e6',
          timeVisible: true,
          secondsVisible: false,
          rightOffset: isMobile ? 2 : 6,
          tickMarkFormatter: (time: number) => {
            // 把 UTC 时间转成本地时区
            const date = new Date(time * 1000)
            // 直接用 UTC 时间
            const hours = date.getUTCHours()
            const minutes = date.getUTCMinutes()

            if (hours === 0 && minutes === 0) {
              return date.toLocaleDateString('en-US', {
                day: 'numeric'
              })
            } else {
              return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
            }
          }
        },
        localization: {
          locale: 'en-US',
          timeFormatter: (time: number) => {
            const date = new Date(time * 1000)
            return date.toLocaleTimeString('en-US', {
              year: 'numeric',
              month: 'short',
              day: '2-digit',
              hour: 'numeric',
              minute: 'numeric'
            })
          },
          priceFormatter: (price: number) => {
            // 如果价格小于0.00001，则显示0.00000
            if (price < 0.001) {
              return price.toFixed(7)
            } else {
              return price.toFixed(5)
            }
          }
        }
      })

      const candlestickSeries = chart.addCandlestickSeries({
        priceFormat: {
          type: 'price',
          precision: 8,
          minMove: 0.00000001
        },
        upColor: theme === 'dark' ? '#00d4aa' : '#26a69a',
        downColor: theme === 'dark' ? '#ff6b6b' : '#ef5350',
        borderDownColor: theme === 'dark' ? '#ff6b6b' : '#ef5350',
        borderUpColor: theme === 'dark' ? '#00d4aa' : '#26a69a',
        wickDownColor: theme === 'dark' ? '#ff6b6b' : '#ef5350',
        wickUpColor: theme === 'dark' ? '#00d4aa' : '#26a69a'
      })

      chartRef.current = chart
      seriesRef.current = candlestickSeries

      // 添加鼠标悬停事件
      chart.subscribeCrosshairMove(param => {
        if (param.point === undefined || !param.time || param.point.x < 0 || param.point.y < 0) {
          setHoveredOHLC(null)
          return
        }

        const data = seriesRef.current ? param.seriesData.get(seriesRef.current) : null
        if (data && 'open' in data) {
          const candlestickData = data as any

          // 悬停时显示该K线本身的涨跌幅（相对于该K线的开盘价）
          const change = candlestickData.close - candlestickData.open
          const changePercent = (change / candlestickData.open) * 100

          setHoveredOHLC({
            open: candlestickData.open,
            high: candlestickData.high,
            low: candlestickData.low,
            close: candlestickData.close,
            change: change,
            changePercent: changePercent,
            time: param.time as number
          })
        }
      })

      const handleResize = () => {
        if (chartContainerRef.current && chartRef.current) {
          chartRef.current.applyOptions({
            width: chartContainerRef.current.clientWidth,
            height: chartContainerRef.current.clientHeight
          })
        }
      }

      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
        if (chartRef.current) {
          chartRef.current.remove()
          chartRef.current = null
        }
      }
    } catch (error) {
      console.error('Error initializing chart:', error)
      setError(`Error initializing chart: ${(error as Error).message}`)
      return undefined
    }
  }

  // 更新时间间隔
  const handleTimeframeChange = async (newTimeframe: string) => {
    setCurrentTimeframe(newTimeframe)
    setCurrentOHLC(null)
    setHoveredOHLC(null)
    setIsDropdownOpen(false)
  }

  useLayoutEffect(() => {
    testUDFConnection()
  }, [pairAddress])

  // 点击外部关闭下拉菜单
  useLayoutEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const timeframeSelector = document.querySelector('[data-timeframe-selector]')

      if (isDropdownOpen && timeframeSelector && !timeframeSelector.contains(target)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  useLayoutEffect(() => {
    // 等待Modal渲染完成后再初始化图表
    // 清理之前的图表
    if (chartRef.current) {
      chartRef.current.remove()
      chartRef.current = null
    }
    if (seriesRef.current) {
      seriesRef.current = null
    }

    setIsLoading(true)
    const timer = setTimeout(() => {
      if (!chartContainerRef.current) {
        console.log('Chart container not ready, retrying...')
        return
      }
      const cleanup = initializeChart()

      const loadInitialData = async () => {
        try {
          // 清理之前的图表

          setIsLoading(true)
          const data = await fetchKlineData(currentTimeframe, shouldInvertPrices)
          if (seriesRef.current && data.length > 0) {
            const candlestickData: CandlestickData[] = data.map(item => ({
              time: item.time as Time,
              open: item.open,
              high: item.high,
              low: item.low,
              close: item.close
            }))
            if (seriesRef.current) {
              seriesRef.current.setData(candlestickData)
            }
          }
          setIsLoading(false)
        } catch (error) {
          console.error('Error loading initial data:', error)
          setError(`Error loading initial data: ${(error as Error).message}`)
          setIsLoading(false)
        }
      }

      setTimeout(() => {
        loadInitialData()
      }, 500)

      return cleanup
    }, 500)

    return () => {
      clearTimeout(timer)
    }
  }, [theme, currentTimeframe, pairAddress, symbol, inputToken, outputToken, isMobile, shouldInvertPrices])

  if (error) {
    return (
      <TradingViewContainer style={{ height: `${height}px` }}>
        <ErrorContainer>{error}</ErrorContainer>
      </TradingViewContainer>
    )
  }

  // 显示的数据（悬停时显示悬停数据，否则显示当前数据）
  const displayData = hoveredOHLC || currentOHLC

  // 根据屏幕宽度条件性渲染
  if (isMobile) {
    return (
      <Modal
        isOpen={true}
        onDismiss={() => {
          onShowChart?.()
        }}
      >
        <TradingViewContainer style={{ height: `80vh` }}>
          {/* 横线 */}
          <HorizontalLine />
          {/* 图表头部信息 */}
          <ChartHeader>
            <TokenPairInfo>
              <TokenIcons>
                <TokenIcon color="#000" zIndex={1}>
                  {inputToken?.symbol && (inputToken.symbol.includes('MARS') || inputToken.symbol.includes('MOVA')) ? (
                    <img src={MarsLogo} alt="MOVA" width={24} height={24} />
                  ) : inputToken?.symbol ? (
                    inputToken.symbol.slice(0, 1)
                  ) : (
                    'T'
                  )}
                </TokenIcon>
                <TokenIcon color="#231f20" zIndex={2}>
                  {outputToken?.symbol &&
                  (outputToken.symbol.includes('MARS') || outputToken.symbol.includes('MOVA')) ? (
                    <img src={MarsLogo} alt="MOVA" width={24} height={24} />
                  ) : outputToken?.symbol ? (
                    outputToken.symbol.slice(0, 1)
                  ) : (
                    'T'
                  )}
                </TokenIcon>
              </TokenIcons>
              <PairName>
                {inputToken?.symbol}/{outputToken?.symbol}
              </PairName>
              <SwapIconWrapper>
                <SwapIcon src={`/images/swap_icon.svg`} alt="arrow-down" />
              </SwapIconWrapper>
            </TokenPairInfo>

            <StatsContainer>
              <StatItem>
                <StatLabel>24h Change</StatLabel>
                <StatValue isChange positive={priceChangePercent24h >= 0}>
                  {priceChangePercent24h >= 0 ? '+' : ''}
                  {priceChangePercent24h.toFixed(2)}%
                </StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>24h High</StatLabel>
                <StatValue>{high24h.toFixed(6)}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>24h Low</StatLabel>
                <StatValue>{low24h.toFixed(6)}</StatValue>
              </StatItem>
              <CurrentPrice>{currentPrice ? currentPrice.toFixed(6) : '--'}</CurrentPrice>
            </StatsContainer>
          </ChartHeader>

          {!isLoading && (
            <TimeframeSelector data-timeframe-selector>
              <SelectButton onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                {timeframes.find(tf => tf.value === currentTimeframe)?.value || '1h'}
              </SelectButton>
              <SelectMenu isOpen={isDropdownOpen}>
                {timeframes.map(tf => {
                  return (
                    <SelectItem
                      key={tf.value}
                      active={currentTimeframe === tf.value}
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleTimeframeChange(tf.value)
                      }}
                    >
                      {tf.label}
                    </SelectItem>
                  )
                })}
              </SelectMenu>
            </TimeframeSelector>
          )}

          <ChartContainer ref={chartContainerRef}>
            {isLoading && <LoadingContainer>Loading...</LoadingContainer>}

            {/* OHLC显示 */}
            {displayData && (
              <OHLCContainer>
                <TradingPairInfo>
                  <PairSymbol>
                    {inputToken?.symbol}/{outputToken?.symbol}
                  </PairSymbol>
                  <TimeframeDot>· {currentTimeframe} ·</TimeframeDot>
                  <PlatformName>USD1Swap</PlatformName>
                  <StatusIndicator />
                </TradingPairInfo>
                <OHLCValues>
                  <OHLCItem isPositive={displayData.close >= displayData.open}>
                    <span style={{ color: '#fff' }}>O:</span> {displayData.open.toFixed(5)}
                  </OHLCItem>
                  <OHLCItem isPositive={displayData.close >= displayData.open}>
                    <span style={{ color: '#fff' }}>H:</span> {displayData.high.toFixed(5)}
                  </OHLCItem>
                  <OHLCItem isPositive={displayData.close >= displayData.open}>
                    <span style={{ color: '#fff' }}>L:</span> {displayData.low.toFixed(5)}
                  </OHLCItem>
                  <OHLCItem isPositive={displayData.close >= displayData.open}>
                    <span style={{ color: '#fff' }}>C:</span> {displayData.close.toFixed(5)}
                  </OHLCItem>
                  <ChangeValue positive={displayData.change >= 0}>
                    {displayData.change >= 0 ? '+' : '-'}
                    {Math.abs(displayData.change).toFixed(6)} ({displayData.changePercent >= 0 ? '+' : '-'}
                    {Math.abs(displayData.changePercent).toFixed(2)}%)
                  </ChangeValue>
                </OHLCValues>
              </OHLCContainer>
            )}
          </ChartContainer>
        </TradingViewContainer>
      </Modal>
    )
  }

  // PC端直接显示TradingViewContainer
  return (
    <TradingViewContainer style={{ height: `${height}px` }}>
      {/* 图表头部信息 */}
      <ChartHeader>
        <TokenPairInfo>
          <TokenIcons>
            <TokenIcon color="#000" zIndex={1}>
              {inputToken?.symbol && (inputToken.symbol.includes('MARS') || inputToken.symbol.includes('MOVA')) ? (
                <img src={MarsLogo} alt="MOVA" width={24} height={24} />
              ) : inputToken?.symbol ? (
                inputToken.symbol.slice(0, 1)
              ) : (
                'T'
              )}
            </TokenIcon>
            <TokenIcon color="#231f20" zIndex={2}>
              {outputToken?.symbol && (outputToken.symbol.includes('MARS') || outputToken.symbol.includes('MOVA')) ? (
                <img src={MarsLogo} alt="MOVA" width={24} height={24} />
              ) : outputToken?.symbol ? (
                outputToken.symbol.slice(0, 1)
              ) : (
                'T'
              )}
            </TokenIcon>
          </TokenIcons>
          <PairName>
            {inputToken?.symbol}/{outputToken?.symbol}
          </PairName>
          <SwapIconWrapper>
            <SwapIcon src={`/images/swap_icon.svg`} alt="arrow-down" />
          </SwapIconWrapper>
        </TokenPairInfo>

        <StatsContainer>
          <StatItem>
            <StatLabel>24h Change</StatLabel>
            <StatValue isChange positive={priceChangePercent24h >= 0}>
              {priceChangePercent24h >= 0 ? '+' : ''}
              {priceChangePercent24h.toFixed(2)}%
            </StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>24h High</StatLabel>
            <StatValue>{high24h.toFixed(6)}</StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>24h Low</StatLabel>
            <StatValue>{low24h.toFixed(6)}</StatValue>
          </StatItem>
          <CurrentPrice>{currentPrice ? currentPrice.toFixed(6) : '--'}</CurrentPrice>
        </StatsContainer>
      </ChartHeader>

      {!isLoading && (
        <TimeframeSelector data-timeframe-selector>
          <SelectButton onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
            {timeframes.find(tf => tf.value === currentTimeframe)?.value || '1h'}
          </SelectButton>
          <SelectMenu isOpen={isDropdownOpen}>
            {timeframes.map(tf => {
              return (
                <SelectItem
                  key={tf.value}
                  active={currentTimeframe === tf.value}
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleTimeframeChange(tf.value)
                  }}
                >
                  {tf.label}
                </SelectItem>
              )
            })}
          </SelectMenu>
        </TimeframeSelector>
      )}

      <ChartContainer ref={chartContainerRef}>
        {isLoading && <LoadingContainer>Loading...</LoadingContainer>}

        {/* OHLC显示 */}
        {displayData && (
          <OHLCContainer>
            <TradingPairInfo>
              <PairSymbol>
                {inputToken?.symbol}/{outputToken?.symbol}
              </PairSymbol>
              <TimeframeDot>· {currentTimeframe} ·</TimeframeDot>
              <PlatformName>USD1Swap</PlatformName>
              <StatusIndicator />
            </TradingPairInfo>
            <OHLCValues>
              <OHLCItem isPositive={displayData.close >= displayData.open}>O: {displayData.open.toFixed(5)}</OHLCItem>
              <OHLCItem isPositive={displayData.close >= displayData.open}>H: {displayData.high.toFixed(5)}</OHLCItem>
              <OHLCItem isPositive={displayData.close >= displayData.open}>L: {displayData.low.toFixed(5)}</OHLCItem>
              <OHLCItem isPositive={displayData.close >= displayData.open}>C: {displayData.close.toFixed(5)}</OHLCItem>
              <ChangeValue positive={displayData.change >= 0}>
                {displayData.change >= 0 ? '+' : '-'}
                {Math.abs(displayData.change).toFixed(6)} ({displayData.changePercent >= 0 ? '+' : '-'}
                {Math.abs(displayData.changePercent).toFixed(2)}%)
              </ChangeValue>
            </OHLCValues>
          </OHLCContainer>
        )}
      </ChartContainer>
    </TradingViewContainer>
  )
}

export default TradingView
